/**
 * صفِ صحنه — پورتِ Enqueue/Pump در کنترلرهای آنلاینِ یونیتی.
 *
 * قاعده‌ی مرکزیِ آن معماری: سرور اول رویداد می‌فرستد، بعد وضعیت. اگر نما هر
 * چه رسید همان لحظه بکشد، وضعیتِ تازه زیرِ انیمیشنِ رویداد می‌نشیند و کارت
 * پیش از فرود «تلپورت» می‌شود — همان چیزی که وب داشت. این‌جا هر پیام یک
 * گام می‌شود و گام‌ها **پشتِ هم** اجرا می‌شوند؛ وضعیت فقط وقتی می‌نشیند که
 * انیمیشنِ پیش از خودش تمام شده باشد.
 *
 * تک‌نخی است و یک پمپ دارد: اگر پمپ خوابیده، با گامِ تازه بیدار می‌شود.
 *
 * ---
 *
 * و چیزِ دوم، که تماشاچی‌ها با آن سوختند: **آهنگِ صحنه مالِ همین‌جاست، پس
 * جمع‌کردنِ عقب‌ماندگی هم همین‌جاست.**
 *
 * سرور دیگر جلوی خودش را نمی‌گیرد تا صفحه برسد؛ رویداد را همان لحظه
 * می‌فرستد. پیش از این نمی‌فرستاد و مکثش دقیقاً اندازه‌ی وقتِ انیمیشن بود —
 * یعنی حاشیه‌ی صفر، و هر تپقِ کوچکی (یک فریمِ کند، زبانه‌ی پس‌زمینه، یک GC)
 * روی هم می‌نشست و هیچ‌وقت پس داده نمی‌شد. بدتر: آن مکث با عددِ ثابتِ ۱٫۷
 * حساب می‌شد، پس از چهار پله‌ی سرعتِ بازیکن فقط یکی درست درمی‌آمد.
 *
 * حالا صف خودش را می‌سنجد و دو کار می‌کند:
 *
 *   ۱. **می‌دود.** هرچه گامِ سرِ صف کهنه‌تر باشد، انیمیشن‌ها نرم‌تر فشرده
 *      می‌شوند (تا یک‌چهارم). عقب‌ماندگی پس داده می‌شود، نه انباشته.
 *   ۲. **می‌پرد** — فقط وقتی دویدن هم جواب نداده. وضعیت **عکسِ کامل** است، نه
 *      دلتا، پس هرچه پیش از آخرین وضعیت مانده دور ریختنی است.
 *
 * عقب‌ماندگی با **زمان** سنجیده می‌شود و نه شمارِ گام‌ها: چند رویداد پشتِ هم
 * حالا عادی است (سه رباتِ میز بینِ دو نوبتِ آدم یک‌جا حرکت می‌کنند) و خودش
 * نشانه‌ی جاماندن نیست.
 */
import { setCatchUp } from "../game/speed";

export type Step = () => Promise<void> | void;

type Item = { run: Step; snap: boolean; at: number };

const now = (): number =>
  typeof performance !== "undefined" ? performance.now() : Date.now();

/**
 * عقب‌ماندگی با **زمان** سنجیده می‌شود و نه با شمارِ گام‌ها.
 *
 * چون سرور دیگر آهنگ را نگه نمی‌دارد، چند رویداد پشتِ هم رسیدن عادی است —
 * سه رباتِ میز بینِ دو نوبتِ آدم یک‌جا حرکت می‌کنند. شمارشِ گام این را
 * «عقب‌ماندگی» می‌دید و انیمیشن را می‌پراند. آن‌چه واقعاً اهمیت دارد این
 * است که چیزی که دارم نشان می‌دهم چقدر کهنه است.
 */
/** تا این‌جا هیچ خبری نیست؛ ریتمِ عادی دست‌نخورده می‌مانَد. */
const EaseMs = 1200;
/** از این‌جا با بیشینه‌ی فشردگی می‌دویم. */
const RushMs = 6000;
/** تندترین حالت — از این بیشتر، انیمیشن دیگر انیمیشن نیست. */
const MinK = 0.25;
/**
 * از این کهنه‌تر یعنی دویدن هم جواب نداده: بپر.
 *
 * تماشاچی‌ای که تازه رسیده، زبانه‌ای که نیم‌ساعت پس‌زمینه بوده، یا شبکه‌ای
 * که یک‌باره همه‌چیز را با هم تحویل داده. این‌جا دیگر انیمیشن بی‌معناست.
 */
const SkipMs = 12000;

export class Sequencer {
  private q: Item[] = [];
  private running = false;
  private dead = false;
  /**
   * پرده‌ای که صف پشتش منتظر است — پرده‌ی شرطِ آغازِ میز.
   *
   * صحنه همان لحظه ساخته می‌شود و پیام‌ها از همان لحظه می‌آیند (وگرنه رویدادِ
   * وسطِ پرده گم می‌شد)، ولی *کشیدنشان* تا بالا رفتنِ پرده صبر می‌کند: آس‌کشی
   * و پخشِ ورق زیرِ پرده‌ی سه‌ثانیه‌ای انجام می‌شد و بازیکن میزی را می‌دید که
   * ورق‌ها همین‌طوری در دستش سبز شده‌اند.
   */
  private gate: Promise<void> | null = null;
  /** چند بار از عقب‌ماندگی پریده‌ایم — برای اشکال‌زدایی از کنسول. */
  skipped = 0;

  /**
   * صف را تا پایانِ `p` نگه دار.
   *
   * انتظارِ پرده «عقب‌ماندگی» نیست، پس سرِ بالا رفتنش ساعتِ همه‌ی گام‌های در
   * صف از نو کوک می‌شود؛ وگرنه همان `lag` که برای شبکه‌ی کند ساخته شده بود
   * اولین انیمیشن‌های میز را می‌فشرد یا می‌پراند.
   */
  hold(p: Promise<void>): void {
    if (this.dead) return;
    this.gate = p.then(() => undefined, () => undefined);
    if (!this.running && this.q.length > 0) void this.pump();
  }

  /** گامِ انیمیشن. اگر صف عقب بیفتد، پریدنی است. */
  enqueue(step: Step): void { this.push({ run: step, snap: false, at: now() }); }

  /**
   * گامِ وضعیت — عکسِ کاملِ میز.
   *
   * جدا از انیمیشن است چون همین تفکیک است که رسیدن را ممکن می‌کند: صف
   * می‌داند از کجا می‌تواند بپرد بی‌آن‌که چیزی گم شود.
   */
  enqueueSnapshot(step: Step): void { this.push({ run: step, snap: true, at: now() }); }

  private push(it: Item): void {
    if (this.dead) return;
    this.q.push(it);
    if (!this.gate) this.catchUp();
    if (!this.running) void this.pump();
  }

  /** ساعتِ گام‌های در صف را از نو کوک کن — بعد از پرده‌ای که خودمان نگه داشتیم. */
  private rebase(): void {
    const t = now();
    for (const it of this.q) it.at = t;
  }

  /** چند میلی‌ثانیه است که گامِ سرِ صف منتظرِ نمایش مانده. */
  private lag(): number {
    const head = this.q[0];
    return head ? now() - head.at : 0;
  }

  /**
   * عقب‌ماندگی را به ضریبِ سرعت ترجمه کن.
   *
   * نرم، چون پرشِ ناگهانیِ سرعت بیشتر از خودِ عقب‌ماندگی به چشم می‌آید.
   */
  private pace(): void {
    const l = this.lag();
    if (l <= EaseMs) { setCatchUp(1); return; }
    const t = Math.min(1, (l - EaseMs) / (RushMs - EaseMs));
    setCatchUp(1 - t * (1 - MinK));
  }

  /**
   * عقب‌ماندگی را ببلع: تا آخرین عکسِ کامل جلو بپر.
   *
   * هرچه پیش از آن مانده — انیمیشن‌ها و عکس‌های کهنه‌تر — چیزی به صفحه
   * اضافه نمی‌کند که خودِ آن عکس نداشته باشد.
   */
  private catchUp(): void {
    if (this.lag() < SkipMs) return;
    let last = -1;
    for (let i = this.q.length - 1; i >= 0; i--) {
      if (this.q[i]!.snap) { last = i; break; }
    }
    // بی هیچ عکسی نمی‌شود پرید: آن‌وقت نمی‌دانیم میز کجاست.
    if (last <= 0) return;
    this.skipped += last;
    this.q.splice(0, last);
  }

  /** همه‌ی گام‌های در صف را دور می‌ریزد (مثلاً سرِ خروج از میز). */
  clear(): void { this.q.length = 0; }

  /** بستنِ صف برای همیشه — گام‌های بعدی نادیده می‌شوند. */
  dispose(): void { this.dead = true; this.gate = null; this.clear(); }

  get pending(): number { return this.q.length; }

  private async pump(): Promise<void> {
    this.running = true;
    try {
      while (this.q.length > 0 && !this.dead) {
        if (this.gate) {
          const g = this.gate;
          await g;
          if (this.gate === g) this.gate = null;
          this.rebase();
          if (this.dead) break;
        }
        this.pace();
        const step = this.q.shift()!;
        try { await step.run(); }
        catch (e) { console.error("[scene]", e); }   // یک گامِ خراب بقیه را نمی‌کشد
      }
    } finally {
      this.running = false;
      // صف که خالی شد، رسیده‌ایم: سرعت به همان چیزی که بازیکن خواسته برگردد.
      setCatchUp(1);
    }
  }
}
